/**
 * Floating Menu Web Component
 *
 * Handles positioning and visibility for dropdown menus that need to escape
 * overflow:hidden containers. Uses fixed positioning with viewport-relative coordinates.
 *
 * Responsibilities:
 * - Toggle menu visibility on trigger click
 * - Calculate and apply fixed positioning based on trigger location
 * - Position arrow indicator to point at trigger
 * - Close menu on outside click, scroll, or Escape key
 * - Keyboard navigation (ArrowUp/Down, Home, End)
 * - Focus management (focus first item on open, return focus on close)
 * - Manage aria-expanded on trigger for accessibility
 *
 * Expects two children with classes: .floating-menu-trigger and .floating-menu-content
 * Menu content should contain an optional .floating-menu-arrow for the pointer indicator.
 *
 * See Components.FloatingMenu for the Elm API.
 */

const TRIGGER_GAP = 12;
const VIEWPORT_PADDING = 8;
const ARROW_SIZE = 7;

class FloatingMenu extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onMenuItemClick = this._onMenuItemClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
  }

  connectedCallback() {
    this._trigger = this.querySelector(".floating-menu-trigger");
    this._content = this.querySelector(".floating-menu-content");

    if (this._trigger && this._content) {
      // Set initial styles on content
      this._content.style.position = "fixed";
      this._content.style.zIndex = "9999";
      this._content.style.visibility = "hidden";
      this._content.style.pointerEvents = "none";

      // Set ARIA role on menu content
      this._content.setAttribute("role", "menu");

      // Set initial ARIA state on trigger
      this._trigger.setAttribute("aria-expanded", "false");
      this._trigger.setAttribute("aria-haspopup", "menu");

      // Add event listeners
      this._trigger.addEventListener("click", this._onTriggerClick);
      this._onKeyDown = this._onKeyDown.bind(this);
    }
  }

  disconnectedCallback() {
    if (this._trigger) {
      this._trigger.removeEventListener("click", this._onTriggerClick);
    }
    this._removeGlobalListeners();
  }

  _onTriggerClick(e) {
    e.stopPropagation();
    if (this._isOpen) {
      this._close();
    } else {
      this._open();
    }
  }

  _open() {
    if (!this._content || !this._trigger) return;

    const triggerRect = this._trigger.getBoundingClientRect();
    const contentRect = this._content.getBoundingClientRect();
    const isRtl = getComputedStyle(this).direction === "rtl";

    let left, arrowDirection;
    let top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;

    if (isRtl) {
      // RTL: Position to the right of the trigger by default
      left = triggerRect.right + TRIGGER_GAP;
      arrowDirection = "left"; // Arrow points left toward trigger

      // If it would go off the right edge, position to the left instead
      if (left + contentRect.width > window.innerWidth - VIEWPORT_PADDING) {
        left = triggerRect.left - contentRect.width - TRIGGER_GAP;
        arrowDirection = "right";
      }
    } else {
      // LTR: Position to the left of the trigger by default
      left = triggerRect.left - contentRect.width - TRIGGER_GAP;
      arrowDirection = "right"; // Arrow points right toward trigger

      // If it would go off the left edge, position to the right instead
      if (left < VIEWPORT_PADDING) {
        left = triggerRect.right + TRIGGER_GAP;
        arrowDirection = "left";
      }
    }

    // Keep within viewport vertically
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    } else if (top + contentRect.height > window.innerHeight - VIEWPORT_PADDING) {
      top = window.innerHeight - contentRect.height - VIEWPORT_PADDING;
    }

    this._content.style.left = `${left}px`;
    this._content.style.top = `${top}px`;
    this._content.style.visibility = "visible";
    this._content.style.pointerEvents = "auto";

    // Position the arrow
    this._positionArrow(triggerRect, top, arrowDirection);

    this._isOpen = true;
    this._trigger.setAttribute("aria-expanded", "true");

    // Add global listeners to close on outside click or scroll
    document.addEventListener("click", this._onDocumentClick, true);
    document.addEventListener("scroll", this._onScroll, true);
    document.addEventListener("keydown", this._onKeyDown, true);

    // Close menu when a menu item is clicked
    this._content.addEventListener("click", this._onMenuItemClick);

    // Move focus to first enabled menu item
    this._focusFirstMenuItem();
  }

  _focusFirstMenuItem() {
    const firstItem = this._content.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
    if (firstItem) {
      firstItem.focus();
    }
  }

  _getMenuItems() {
    return Array.from(
      this._content.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])'),
    );
  }

  _onKeyDown(e) {
    if (!this._isOpen) return;

    const items = this._getMenuItems();
    const currentIndex = items.indexOf(document.activeElement);

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        this._close();
        this._trigger.focus();
        break;

      case "ArrowDown": {
        e.preventDefault();
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex]?.focus();
        break;
      }

      case "ArrowUp": {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex]?.focus();
        break;
      }

      case "Home":
        e.preventDefault();
        items[0]?.focus();
        break;

      case "End":
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  }

  _onMenuItemClick(e) {
    // Close the menu if a menu item (button or element with role="menuitem") was clicked
    const menuItem = e.target.closest('[role="menuitem"]');
    if (menuItem && !menuItem.hasAttribute("aria-disabled")) {
      this._close();
    }
  }

  _positionArrow(triggerRect, menuTop, direction) {
    const arrow = this._content.querySelector(".floating-menu-arrow");
    if (!arrow) return;

    // Calculate arrow vertical position relative to trigger center
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const arrowTop = triggerCenterY - menuTop - ARROW_SIZE;

    arrow.style.position = "absolute";
    arrow.style.top = `${arrowTop}px`;
    arrow.style.width = "0";
    arrow.style.height = "0";
    arrow.style.borderStyle = "solid";

    if (direction === "right") {
      // Arrow on right side, pointing right
      arrow.style.right = `-${ARROW_SIZE}px`;
      arrow.style.left = "auto";
      arrow.style.borderWidth = `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`;
      arrow.style.borderColor = "transparent transparent transparent rgb(30 41 59)"; // slate-800
    } else {
      // Arrow on left side, pointing left
      arrow.style.left = `-${ARROW_SIZE}px`;
      arrow.style.right = "auto";
      arrow.style.borderWidth = `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`;
      arrow.style.borderColor = "transparent rgb(30 41 59) transparent transparent"; // slate-800
    }
  }

  _close() {
    if (!this._content) return;

    this._content.style.visibility = "hidden";
    this._content.style.pointerEvents = "none";
    this._isOpen = false;
    this._trigger.setAttribute("aria-expanded", "false");

    this._removeGlobalListeners();
  }

  _onDocumentClick(e) {
    // Close if click is outside the menu content
    if (this._content && !this._content.contains(e.target) && !this._trigger.contains(e.target)) {
      this._close();
    }
  }

  _onScroll() {
    // Close on any scroll event
    this._close();
  }

  _removeGlobalListeners() {
    document.removeEventListener("click", this._onDocumentClick, true);
    document.removeEventListener("scroll", this._onScroll, true);
    document.removeEventListener("keydown", this._onKeyDown, true);
    if (this._content) {
      this._content.removeEventListener("click", this._onMenuItemClick);
    }
  }
}

const init = () => {
  if (!customElements.get("floating-menu")) {
    customElements.define("floating-menu", FloatingMenu);
  }
};

export default { init };
